import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Slideshow,
  Error as ErrorIcon,
  Warning,
  Info,
  CheckCircle,
  TrendingUp,
  Link as LinkIcon,
  Lightbulb,
  Psychology,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Deck } from '../../types';
import { formatDateTime } from '../../utils/formatting';

interface DeckOverviewProps {
  deck: Deck;
}

export const DeckOverview: React.FC<DeckOverviewProps> = ({ deck }) => {
  const navigate = useNavigate();
  // TEMPORARY DEBUG - Remove after testing
  console.log('🔍 DeckOverview - Full deck data:', deck);
  console.log('🔍 Has overallReview?', !!deck.overallReview);
  if (deck.overallReview) {
    console.log('🔍 overallReview data:', deck.overallReview);
  } else {
    console.log('⚠️ No overallReview found in deck data');
  }
  
  const totalIssues = deck.issueCount
    ? deck.issueCount.critical + deck.issueCount.high + deck.issueCount.low
    : 0;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography 
        variant="h5" 
        gutterBottom
        title={deck.fileName}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {deck.fileName}
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Chip
          icon={<Slideshow />}
          label={`${deck.totalSlides || 0} Slides`}
          variant="outlined"
        />
        <Chip
          label={`Uploaded: ${formatDateTime(deck.uploadedAt)}`}
          variant="outlined"
        />
        <Chip
          label={`Status: ${deck.status}`}
          color={deck.status === 'completed' ? 'success' : 'warning'}
        />
      </Box>

      {deck.issueCount && (
        <>
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom>
            Issue Summary
          </Typography>
          
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={6} md={3}>
              <Box
                onClick={() => navigate(`/analysis/${deck.deckId}/issues?s=critical`)}
                sx={{
                  p: 2,
                  bgcolor: 'error.light',
                  borderRadius: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { boxShadow: 2, transform: 'translateY(-2px)' },
                }}
                role="button"
                aria-label="View critical issues"
              >
                <ErrorIcon sx={{ fontSize: 40, color: 'error.dark' }} />
                <Typography variant="h4" fontWeight={700}>
                  {deck.issueCount.critical}
                </Typography>
                <Typography variant="body2">Critical</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box
                onClick={() => navigate(`/analysis/${deck.deckId}/issues?s=high`)}
                sx={{
                  p: 2,
                  bgcolor: '#FF9800',
                  borderRadius: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { boxShadow: 2, transform: 'translateY(-2px)' },
                }}
                role="button"
                aria-label="View high issues"
              >
                <Warning sx={{ fontSize: 40, color: '#E65100' }} />
                <Typography variant="h4" fontWeight={700}>
                  {deck.issueCount.high}
                </Typography>
                <Typography variant="body2">High</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box
                onClick={() => navigate(`/analysis/${deck.deckId}/issues?s=low`)}
                sx={{
                  p: 2,
                  bgcolor: 'info.light',
                  borderRadius: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { boxShadow: 2, transform: 'translateY(-2px)' },
                }}
                role="button"
                aria-label="View low issues"
              >
                <Info sx={{ fontSize: 40, color: 'info.dark' }} />
                <Typography variant="h4" fontWeight={700}>
                  {deck.issueCount.low}
                </Typography>
                <Typography variant="body2">Low</Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box mt={2} textAlign="center">
            <Typography variant="h6" color="text.secondary">
              Total Issues: {totalIssues}
            </Typography>
          </Box>
        </>
      )}

      {/* TEMPORARY: Show debug info if overallReview is missing */}
      {!deck.overallReview && deck.status === 'completed' && (
        <>
          <Divider sx={{ my: 3 }} />
          <Alert severity="info">
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Overall Deck Analysis Not Available
            </Typography>
            <Typography variant="body2" paragraph>
              The overall AI analysis was not found for this deck. This could mean:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <li><Typography variant="body2">This deck was analyzed before the overall analysis feature was added</Typography></li>
              <li><Typography variant="body2">The backend needs to be redeployed with the latest code</Typography></li>
              <li><Typography variant="body2">The overall analysis failed during processing (check logs)</Typography></li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Solution:</strong> Upload a new presentation or redeploy the backend with: <code>deploy_dev.bat</code>
            </Typography>
          </Alert>
        </>
      )}

      {deck.overallReview && (
        <>
          <Divider sx={{ my: 3 }} />
          
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">
              <Psychology sx={{ fontSize: 24, mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Overall AI Analysis
            </Typography>
            {(deck.overallReview.overallScore || deck.overallReview.overallScore === 0) && (
              <Chip
                label={`Score: ${deck.overallReview.overallScore}/10`}
                color={deck.overallReview.overallScore >= 7 ? 'success' : deck.overallReview.overallScore >= 5 ? 'warning' : 'error'}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>

          {/* Scores Section */}
          {(deck.overallReview.coherenceScore !== undefined || deck.overallReview.impactScore !== undefined) && (
            <Grid container spacing={2} mb={3}>
              {deck.overallReview.coherenceScore !== undefined && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Coherence Score
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress
                        variant="determinate"
                        value={deck.overallReview.coherenceScore * 10}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                        color={deck.overallReview.coherenceScore >= 7 ? 'success' : 'warning'}
                      />
                      <Typography variant="h6" fontWeight={700}>
                        {deck.overallReview.coherenceScore}/10
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {deck.overallReview.impactScore !== undefined && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Impact Score
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress
                        variant="determinate"
                        value={deck.overallReview.impactScore * 10}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                        color={deck.overallReview.impactScore >= 7 ? 'success' : 'warning'}
                      />
                      <Typography variant="h6" fontWeight={700}>
                        {deck.overallReview.impactScore}/10
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
          
          <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {deck.overallReview.summary}
          </Typography>

          <Grid container spacing={2} mb={2}>
            {deck.overallReview.keyStrengths && deck.overallReview.keyStrengths.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.light' }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    <CheckCircle sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                    Key Strengths
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {deck.overallReview.keyStrengths.map((strength, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{strength}</Typography>
                      </li>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}

            {deck.overallReview.criticalIssues && deck.overallReview.criticalIssues.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'error.lighter', border: '1px solid', borderColor: 'error.light' }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    <ErrorIcon sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle', color: 'error.main' }} />
                    Critical Issues
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {deck.overallReview.criticalIssues.map((issue, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{issue}</Typography>
                      </li>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>

          {deck.overallReview.slideRelationships && deck.overallReview.slideRelationships.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                <LinkIcon sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle', color: 'info.main' }} />
                Slide Relationships & Flow
              </Typography>
              <Grid container spacing={1}>
                {deck.overallReview.slideRelationships.map((rel, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Alert
                      severity={rel.effectiveness === 'high' ? 'success' : rel.effectiveness === 'medium' ? 'warning' : 'error'}
                      icon={false}
                      sx={{ py: 0.5 }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {rel.slideRange}
                        <Chip
                          label={rel.effectiveness}
                          size="small"
                          color={rel.effectiveness === 'high' ? 'success' : rel.effectiveness === 'medium' ? 'warning' : 'error'}
                          sx={{ ml: 1, height: 20 }}
                        />
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {rel.relationship}
                      </Typography>
                    </Alert>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {deck.overallReview.recommendations && deck.overallReview.recommendations.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                <Lightbulb sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                Strategic Recommendations
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                {deck.overallReview.recommendations.map((rec, idx) => (
                  <li key={idx}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{rec}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

